import { useCallback, useMemo, useState } from 'react';

import { useTracking } from '@change-corgi/core/react/tracking';

import type { ReadyToSharePosts } from 'src/app/shared/api/readyToShare';

import { AVAILABLE_PLATFORMS, type PlatformGroupedPosts, type PlatformType } from '../../utils/platformGrouping';
import { useHandlePollingPreDrafted } from '../useHandlePollingPreDrafted';

type UseReadyToSharePreDraftedResult = ModelHookResult<
	{
		isOpen: boolean;
		shouldShowReadyToShareExperiment: boolean;
		isLoading: boolean;
		loadingStates: Record<PlatformType, boolean>;
		readyToSharePostsValue?: ReadyToSharePosts;
		groupedPosts: PlatformGroupedPosts;
		selectedPlatform: PlatformType;
	},
	{
		openSharePreviewModal: () => void;
		closeSharePreviewModal: () => void;
		trackView: (currentPosition: string) => void;
		selectPlatform: (platform: PlatformType) => void;
	}
>;

export const PRE_MADE_POST_MODULE_VIEW_EVENT_NAME = 'petition_manage_ready_to_share_posts_view_v2_pre_drafted';
export const PRE_MADE_POST_MODULE_CLICK_EVENT_NAME = 'petition_manage_ready_to_share_posts_click_v2_pre_drafted';
export const PRE_MADE_POST_MODULE_EVENT_COMMON_PROPERTIES = {
	context: 'petition_dashboard_ready_to_share_post_pre_drafted',
	view_context: 'petition_dashboard_ready_to_share_post_pre_drafted',
};

export function useReadyToSharePreDrafted(): UseReadyToSharePreDraftedResult {
	const [isOpen, setOpen] = useState(false);
	const [isTracked, setIsTracked] = useState(false);
	const [selectedPlatform, setSelectedPlatform] = useState<PlatformType>(AVAILABLE_PLATFORMS[0] || 'facebook');
	const track = useTracking();
	const data = useHandlePollingPreDrafted();

	// Compute isLoading: true if ANY platform is still loading
	const isLoading = useMemo(
		() => Object.values(data.loadingStates).some((platformLoading) => platformLoading),
		[data.loadingStates],
	);

	const trackView = useCallback(
		(currentPosition: string) => {
			if (isTracked || currentPosition === 'below') {
				return;
			}
			void track(PRE_MADE_POST_MODULE_VIEW_EVENT_NAME, PRE_MADE_POST_MODULE_EVENT_COMMON_PROPERTIES);
			setIsTracked(true);
		},
		[track, setIsTracked, isTracked],
	);

	const openSharePreviewModal = useCallback(() => {
		setOpen(true);
		void track(PRE_MADE_POST_MODULE_CLICK_EVENT_NAME, PRE_MADE_POST_MODULE_EVENT_COMMON_PROPERTIES);
	}, [setOpen, track]);

	const closeSharePreviewModal = useCallback(() => {
		setOpen(false);
	}, [setOpen]);

	const selectPlatform = useCallback((platform: PlatformType) => {
		setSelectedPlatform(platform);
	}, []);

	return {
		data: {
			isOpen,
			isLoading,
			selectedPlatform,
			loadingStates: data.loadingStates,
			shouldShowReadyToShareExperiment: data.shouldShowReadyToShareExperiment,
			groupedPosts: data.groupedPosts,
		},
		actions: {
			openSharePreviewModal,
			closeSharePreviewModal,
			trackView,
			selectPlatform,
		},
	};
}
